import { useEffect, useRef, useState } from 'react'
import { Button, Card, CardContent, CardDescription, CardHeader, CardTitle, cn } from '@saas-pages/shared/ui'
import { toolError, toolInvoke, toolSuccess } from '@saas-pages/shared/public/runtime'
import { format, useFormatBytes, useT, type Translations } from '@/shared/i18n'
import { getUtilityDefinition } from '@/shared/utilities/utilities'
import {
  MAX_COMPARE_PAGES,
  MAX_FILE_MB,
  comparePdfText,
  extractComparePdfText,
  validateFileSize,
  type CompareProgress,
  type CompareResult,
  type PageDiff,
} from './lib'
import { FilePickPrompt } from '@/shared/ui/FilePickPrompt'

const UTILITY = getUtilityDefinition('compare-pdf').analyticsKey

type Slot = 'left' | 'right'

type Phase =
  | { kind: 'idle'; left: File | null; right: File | null }
  | { kind: 'loading'; left: File; right: File; progress: CompareProgress }
  | { kind: 'success'; result: CompareResult }
  | { kind: 'error'; left: File | null; right: File | null; message: string; code: string }

function initialPhase(): Phase {
  return { kind: 'idle', left: null, right: null }
}

interface DropZoneProps {
  slot: Slot
  file: File | null
  disabled: boolean
  onFile: (slot: Slot, file: File) => void
  onClear: (slot: Slot) => void
}

function DropZone({ slot, file, disabled, onFile, onClear }: DropZoneProps) {
  const t = useT()
  const inputRef = useRef<HTMLInputElement>(null)
  const formatBytes = useFormatBytes()
  const [dragging, setDragging] = useState(false)
  const label = slot === 'left' ? t.comparePdf.leftFile : t.comparePdf.rightFile

  function handleFiles(files: FileList | null) {
    if (!files || files.length === 0) return
    const candidate = files[0]
    if (!candidate || !candidate.name.toLowerCase().endsWith('.pdf')) return
    onFile(slot, candidate)
  }

  if (file) {
    return (
      <div className="rounded-lg border bg-accent/30 px-4 py-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-xs font-semibold text-primary">
            PDF
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-medium uppercase text-muted-foreground">{label}</p>
            <p className="truncate text-sm font-medium text-foreground">{file.name}</p>
            <p className="text-xs text-muted-foreground">{formatBytes(file.size)}</p>
          </div>
          <button
            type="button"
            onClick={() => onClear(slot)}
            disabled={disabled}
            aria-label={t.common.deleteFile}
            className={cn(
              'shrink-0 rounded p-1 text-muted-foreground transition-colors',
              'hover:bg-destructive/10 hover:text-destructive',
              disabled && 'pointer-events-none opacity-50',
            )}
          >
            x
          </button>
        </div>
      </div>
    )
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : 0}
      aria-label={label}
      onClick={() => !disabled && inputRef.current?.click()}
      onKeyDown={(event) => {
        if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
          inputRef.current?.click()
        }
      }}
      onDragOver={(event) => {
        event.preventDefault()
        if (!disabled) setDragging(true)
      }}
      onDragLeave={() => setDragging(false)}
      onDrop={(event) => {
        event.preventDefault()
        setDragging(false)
        if (!disabled) handleFiles(event.dataTransfer.files)
      }}
      className={cn(
        'flex min-h-40 cursor-pointer select-none flex-col items-center justify-center gap-3',
        'rounded-xl border-2 border-dashed p-6 text-center transition-colors',
        dragging && !disabled
          ? 'border-primary bg-primary/5'
          : 'border-border hover:border-primary/50 hover:bg-accent/30',
        disabled && 'pointer-events-none opacity-50',
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,application/pdf"
        className="sr-only"
        tabIndex={-1}
        disabled={disabled}
        onChange={(event) => {
          handleFiles(event.target.files)
          event.currentTarget.value = ''
        }}
      />
      <div className="text-3xl font-semibold text-primary" aria-hidden>
        PDF
      </div>
      <div>
        <p className="text-sm font-medium text-foreground">{label}</p>
        <p className="mt-1 text-xs text-muted-foreground">
          <FilePickPrompt drag={t.common.dragPdf} />
        </p>
      </div>
    </div>
  )
}

function Spinner() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="animate-spin"
      aria-hidden="true"
    >
      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
    </svg>
  )
}

function ProgressPanel({ progress }: { progress: CompareProgress }) {
  const t = useT()
  const total = progress.total || 1
  const percent = Math.round((progress.current / total) * 100)
  const title =
    progress.stage === 'left'
      ? t.comparePdf.progressLeft
      : progress.stage === 'right'
        ? t.comparePdf.progressRight
        : t.comparePdf.progressCompare

  return (
    <Card>
      <CardContent className="space-y-3 pt-5">
        <div className="flex items-center justify-between text-sm">
          <span className="font-medium text-foreground">{title}</span>
          <span className="text-muted-foreground">
            {progress.stage === 'compare'
              ? t.comparePdf.progressCompare
              : format(t.comparePdf.progressPages, {
                  current: progress.current,
                  total: progress.total,
                })}
          </span>
        </div>
        <div className="h-2 overflow-hidden rounded-full bg-muted">
          <div className="h-full rounded-full bg-primary" style={{ width: `${percent}%` }} />
        </div>
      </CardContent>
    </Card>
  )
}

function ErrorPanel({ message, onRetry }: { message: string; onRetry: () => void }) {
  const t = useT()

  return (
    <Card className="border-destructive/30 bg-destructive/5">
      <CardContent className="pt-5">
        <p className="mb-2 text-sm font-semibold text-destructive">{t.common.errorTitle}</p>
        <p className="mb-4 text-sm text-foreground">{message}</p>
        <Button variant="outline" size="sm" onClick={onRetry}>
          {t.common.retry}
        </Button>
      </CardContent>
    </Card>
  )
}

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border bg-muted/30 px-4 py-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="mt-1 text-lg font-semibold text-foreground">{value}</p>
    </div>
  )
}

function DiffSample({ title, lines, empty }: { title: string; lines: string[]; empty: string }) {
  return (
    <div className="min-w-0 rounded-lg border bg-background">
      <div className="border-b px-3 py-2 text-xs font-medium text-muted-foreground">{title}</div>
      <div className="max-h-48 overflow-auto p-3">
        {lines.length > 0 ? (
          <ul className="space-y-2">
            {lines.map((line, index) => (
              <li key={`${line}-${index}`} className="text-xs leading-5 text-foreground">
                {line}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-xs text-muted-foreground">{empty}</p>
        )}
      </div>
    </div>
  )
}

function DiffCard({ diff }: { diff: PageDiff }) {
  const t = useT()
  const title =
    diff.kind === 'added'
      ? format(t.comparePdf.pageAdded, { page: diff.pageNumber })
      : diff.kind === 'removed'
        ? format(t.comparePdf.pageRemoved, { page: diff.pageNumber })
        : format(t.comparePdf.pageChanged, { page: diff.pageNumber })

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-base">{title}</CardTitle>
        <CardDescription>
          {format(t.comparePdf.diffStats, {
            removed: diff.removedLines,
            added: diff.addedLines,
          })}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <DiffSample
            title={t.comparePdf.removedText}
            lines={diff.removedSample}
            empty={t.comparePdf.noRemovedText}
          />
          <DiffSample
            title={t.comparePdf.addedText}
            lines={diff.addedSample}
            empty={t.comparePdf.noAddedText}
          />
        </div>
        {diff.truncated && (
          <p className="text-xs text-muted-foreground">{t.comparePdf.truncatedDiff}</p>
        )}
      </CardContent>
    </Card>
  )
}

function ResultPanel({ result, onReset }: { result: CompareResult; onReset: () => void }) {
  const t = useT()
  const summary = result.summary
  const previewDiffs = result.diffs.slice(0, 12)

  return (
    <div className="space-y-5">
      <Card>
        <CardContent className="space-y-5 pt-6">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-sm font-semibold text-foreground">
                {summary.status === 'identical'
                  ? t.comparePdf.identicalTitle
                  : t.comparePdf.differentTitle}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                {format(t.comparePdf.resultFiles, {
                  left: result.left.fileName,
                  right: result.right.fileName,
                })}
              </p>
            </div>
            <Button type="button" variant="outline" onClick={onReset}>
              {t.comparePdf.clear}
            </Button>
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard label={t.comparePdf.equalPages} value={summary.equalPages} />
            <StatCard label={t.comparePdf.changedPages} value={summary.changedPages} />
            <StatCard label={t.comparePdf.addedPages} value={summary.addedPages} />
            <StatCard label={t.comparePdf.removedPages} value={summary.removedPages} />
          </div>

          <div className="grid gap-3 sm:grid-cols-3">
            <StatCard
              label={t.comparePdf.pageCount}
              value={`${summary.leftPages} / ${summary.rightPages}`}
            />
            <StatCard
              label={t.comparePdf.wordCount}
              value={`${summary.leftWords} / ${summary.rightWords}`}
            />
            <StatCard
              label={t.comparePdf.similarity}
              value={`${summary.similarityPercent}%`}
            />
          </div>

          {(result.left.warning || result.right.warning) && (
            <div className="rounded-lg border border-amber-300/60 bg-amber-50 px-4 py-3 text-sm text-amber-950 dark:border-amber-500/40 dark:bg-amber-950/30 dark:text-amber-100">
              {t.comparePdf.lowTextWarning}
            </div>
          )}
        </CardContent>
      </Card>

      {previewDiffs.length > 0 && (
        <div className="space-y-3">
          <h2 className="text-lg font-semibold text-foreground">{t.comparePdf.diffHeading}</h2>
          {previewDiffs.map((diff) => (
            <DiffCard key={`${diff.kind}-${diff.pageNumber}`} diff={diff} />
          ))}
          {result.diffs.length > previewDiffs.length && (
            <p className="text-sm text-muted-foreground">
              {format(t.comparePdf.hiddenDiffs, {
                count: result.diffs.length - previewDiffs.length,
              })}
            </p>
          )}
        </div>
      )}
    </div>
  )
}

function classifyError(error: unknown, t: Translations): { code: string; message: string } {
  const text = error instanceof Error ? `${error.name} ${error.message}` : String(error)
  if (/password|encrypted|PasswordException/i.test(text)) {
    return { code: 'encrypted', message: t.comparePdf.encrypted }
  }
  if (/too_many_pages/i.test(text)) {
    return {
      code: 'too_many_pages',
      message: format(t.comparePdf.tooManyPages, { pages: MAX_COMPARE_PAGES }),
    }
  }
  if (/no_text_layer/i.test(text)) {
    return { code: 'no_text_layer', message: t.comparePdf.noTextLayer }
  }
  if (/InvalidPDF|invalid|damaged|corrupt|parse/i.test(text)) {
    return { code: 'invalid_pdf', message: t.comparePdf.invalidPdf }
  }
  return { code: 'compare_error', message: t.comparePdf.compareError }
}

export function SravnitPdfForm() {
  const t = useT()
  const [phase, setPhase] = useState<Phase>(initialPhase)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    return () => abortRef.current?.abort()
  }, [])

  const isLoading = phase.kind === 'loading'
  const leftFile = phase.kind === 'success' ? null : phase.left
  const rightFile = phase.kind === 'success' ? null : phase.right

  function setFile(slot: Slot, file: File) {
    if (!validateFileSize(file)) {
      toolError(UTILITY, 'client_too_large', { side: slot, input_bytes: file.size })
      setPhase((current) => ({
        kind: 'error',
        left: current.kind === 'success' ? null : current.left,
        right: current.kind === 'success' ? null : current.right,
        code: 'client_too_large',
        message: format(t.common.tooLarge, { size: MAX_FILE_MB }),
      }))
      return
    }

    setPhase((current) => ({
      kind: 'idle',
      left: slot === 'left' ? file : current.kind === 'success' ? null : current.left,
      right: slot === 'right' ? file : current.kind === 'success' ? null : current.right,
    }))
  }

  function clearFile(slot: Slot) {
    setPhase((current) => ({
      kind: 'idle',
      left: slot === 'left' ? null : current.kind === 'success' ? null : current.left,
      right: slot === 'right' ? null : current.kind === 'success' ? null : current.right,
    }))
  }

  function reset() {
    abortRef.current?.abort()
    setPhase(initialPhase())
  }

  function retry() {
    setPhase((current) => ({
      kind: 'idle',
      left: current.kind === 'success' ? null : current.left,
      right: current.kind === 'success' ? null : current.right,
    }))
  }

  async function submit() {
    if (phase.kind !== 'idle' || !phase.left || !phase.right) return

    const left = phase.left
    const right = phase.right
    const controller = new AbortController()
    const startMs = Date.now()
    abortRef.current = controller

    setPhase({ kind: 'loading', left, right, progress: { stage: 'left', current: 0, total: 1 } })
    toolInvoke(UTILITY, {
      left_bytes: left.size,
      right_bytes: right.size,
    })

    try {
      const leftText = await extractComparePdfText({
        file: left,
        signal: controller.signal,
        stage: 'left',
        onProgress: (progress) => {
          setPhase((current) => (current.kind === 'loading' ? { ...current, progress } : current))
        },
      })
      const rightText = await extractComparePdfText({
        file: right,
        signal: controller.signal,
        stage: 'right',
        onProgress: (progress) => {
          setPhase((current) => (current.kind === 'loading' ? { ...current, progress } : current))
        },
      })

      setPhase((current) =>
        current.kind === 'loading'
          ? { ...current, progress: { stage: 'compare', current: 1, total: 1 } }
          : current,
      )

      const result = comparePdfText(leftText, rightText)
      const durationMs = Date.now() - startMs
      toolSuccess(UTILITY, durationMs, {
        left_bytes: left.size,
        right_bytes: right.size,
        left_pages: result.summary.leftPages,
        right_pages: result.summary.rightPages,
        changed_pages: result.summary.changedPages,
        added_pages: result.summary.addedPages,
        removed_pages: result.summary.removedPages,
        status: result.summary.status,
      })
      setPhase({ kind: 'success', result })
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') return
      const { code, message } = classifyError(error, t)
      toolError(UTILITY, code, { left_bytes: left.size, right_bytes: right.size })
      setPhase({ kind: 'error', left, right, code, message })
    }
  }

  if (phase.kind === 'success') {
    return <ResultPanel result={phase.result} onReset={reset} />
  }

  return (
    <div className="space-y-5">
      <div className="grid gap-4 sm:grid-cols-2">
        <DropZone
          slot="left"
          file={leftFile}
          disabled={isLoading}
          onFile={setFile}
          onClear={clearFile}
        />
        <DropZone
          slot="right"
          file={rightFile}
          disabled={isLoading}
          onFile={setFile}
          onClear={clearFile}
        />
      </div>

      <div className="rounded-lg border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
        {format(t.comparePdf.scopeNotice, { pages: MAX_COMPARE_PAGES })}
      </div>

      {phase.kind === 'loading' && <ProgressPanel progress={phase.progress} />}
      {phase.kind === 'error' && <ErrorPanel message={phase.message} onRetry={retry} />}

      <Button
        size="lg"
        className="w-full"
        disabled={phase.kind !== 'idle' || !phase.left || !phase.right || isLoading}
        onClick={submit}
      >
        {isLoading ? (
          <>
            <Spinner />
            {t.comparePdf.loading}
          </>
        ) : (
          t.comparePdf.submit
        )}
      </Button>
    </div>
  )
}
